import { ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import type {
  GalleryTaskStartEvent,
  GalleryTaskProgressEvent,
  GalleryTaskCompleteEvent,
  GalleryTaskFailedEvent,
  GalleryTaskCancelEvent,
} from '../../hooks/taskEvents'

export type TaskEventType = 'start' | 'progress' | 'complete' | 'failed' | 'cancel'

export interface TaskEvent {
  type: TaskEventType
  payload: GalleryTaskStartEvent | GalleryTaskProgressEvent | GalleryTaskCompleteEvent | GalleryTaskFailedEvent | GalleryTaskCancelEvent
  /** Auto-incremented to ensure uniqueness for consecutive events */
  seq: number
}

/**
 * 轻量级任务事件 Store，使用事件队列替代单值覆盖。
 *
 * 解决的问题：同一调用栈内连续 emit（如 start→progress）时，
 * 单值 shallowRef 会导致中间事件被 Vue 响应式合并而丢失。
 *
 * 使用方式：
 * - 监听方 watch(taskEventStore.version, ...) 感知变化
 * - 在回调中调用 taskEventStore.consume() 获取并清空所有待处理事件
 */
export const useTaskEventStore = defineStore('board-task-event', () => {
  let _seq = 0
  /** 事件队列：累积所有未消费的事件 */
  const pendingQueue = shallowRef<TaskEvent[]>([])
  /** 版本号：每次 emit 递增，用于触发 watch */
  const version = ref(0)

  // 兼容旧的 latestEvent 接口（供未迁移的消费方使用）
  const latestEvent = shallowRef<TaskEvent | null>(null)

  const emit = (type: TaskEventType, payload: TaskEvent['payload']) => {
    const event: TaskEvent = { type, payload, seq: ++_seq }
    // 追加到队列
    pendingQueue.value = [...pendingQueue.value, event]
    // 递增版本号触发 watch
    version.value++
    // 兼容旧接口
    latestEvent.value = event
  }

  /** 消费并清空队列，返回所有待处理事件 */
  const consume = (): TaskEvent[] => {
    const events = pendingQueue.value
    pendingQueue.value = []
    return events
  }

  /** 查看队列但不清空 */
  const peek = (): readonly TaskEvent[] => {
    return pendingQueue.value
  }

  const reset = () => {
    pendingQueue.value = []
    latestEvent.value = null
    version.value = 0
    _seq = 0
  }

  return { latestEvent, version, emit, consume, peek, reset }
})
